import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api, fmtErr } from "../../lib/api";
import { Anchor, ArrowRight, BadgeCheck, Gift } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { refStore } from "../../lib/affApi";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: params.get("email") || "", password: "", phone: "", company: "",
    account_kind: params.get("claim") ? "provider" : "crew",
    referral_code: (params.get("ref") || refStore.get() || "").toUpperCase(),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [claim, setClaim] = useState(null);
  const [refValid, setRefValid] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Referral code (from /r/CODE, 90-day memory or typed in) — validate live.
  useEffect(() => {
    const code = form.referral_code.trim();
    if (!code) { setRefValid(null); return; }
    const t = setTimeout(() => {
      api.get("/affiliate/code-check", { params: { code } })
        .then(({ data }) => setRefValid(!!data.valid))
        .catch(() => setRefValid(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form.referral_code]);

  // Unified supplier DB: check whether this email matches an unclaimed
  // supplier profile so the user knows they're claiming an existing listing.
  useEffect(() => {
    const email = form.email.trim();
    if (!email || !email.includes("@")) { setClaim(null); return; }
    const t = setTimeout(() => {
      api.get("/auth/claim-check", { params: { email } })
        .then(({ data }) => setClaim(data.supplier_exists ? data : null))
        .catch(() => setClaim(null));
    }, 500);
    return () => clearTimeout(t);
  }, [form.email]);

  // Supplier activation mode: force provider kind and prefill the company
  // name (editable) from the existing supplier profile.
  useEffect(() => {
    if (claim) {
      setForm((f) => ({
        ...f,
        account_kind: "provider",
        company: f.company || claim.company_name || "",
      }));
    }
  }, [claim]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await register({ ...form, referral_code: form.referral_code.trim() || null });
      if (res?.requires_verification) {
        refStore.clear();
        nav(`/verify-email?email=${encodeURIComponent(res.email)}`, { replace: true });
      } else {
        nav("/app", { replace: true });
      }
    } catch (e2) {
      setErr(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-ink">
        <img
          src="https://images.unsplash.com/photo-1648997578921-39af26168d93"
          alt="Luxury yacht"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-ink/80 via-ink/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-white text-ink grid place-items-center">
              <Anchor className="h-4 w-4" />
            </div>
            <span className="font-display text-[17px] font-medium">Seavanto</span>
          </Link>
          <div>
            <div className="font-display text-3xl leading-tight max-w-md">
              Start your 14-day trial. Add one yacht. Invite the captain. Done.
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="register-form">
          <div>
            <h1 className="font-display text-3xl font-medium tracking-tight">Create your account</h1>
            <p className="text-sm text-zinc-600 mt-1">No credit card required.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {!claim && (
            <div className="md:col-span-2">
              <Label>I'm joining as</Label>
              <div className="mt-1 grid grid-cols-2 gap-2" data-testid="reg-kind">
                <button
                  type="button"
                  onClick={() => set("account_kind", "crew")}
                  className={
                    "rounded-md border px-3 py-2 text-sm text-left transition " +
                    (form.account_kind === "crew"
                      ? "border-ink bg-ink/5 ring-1 ring-ink"
                      : "border-zinc-200 hover:border-zinc-400")
                  }
                  data-testid="reg-kind-crew"
                >
                  <div className="font-medium">Yacht crew</div>
                  <div className="text-[11px] text-zinc-600 mt-0.5">
                    Owner / Manager / Captain or working crew on a yacht.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => set("account_kind", "provider")}
                  className={
                    "rounded-md border px-3 py-2 text-sm text-left transition " +
                    (form.account_kind === "provider"
                      ? "border-ink bg-ink/5 ring-1 ring-ink"
                      : "border-zinc-200 hover:border-zinc-400")
                  }
                  data-testid="reg-kind-provider"
                >
                  <div className="font-medium">Service provider</div>
                  <div className="text-[11px] text-zinc-600 mt-0.5">
                    3rd-party vendor — engine tech, paint yard, broker, agent…
                  </div>
                </button>
              </div>
            </div>
            )}
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="reg-name" />
            </div>
            <div>
              <Label htmlFor="company">Company {form.account_kind === "provider" ? "" : "(optional)"}</Label>
              <Input id="company" value={form.company} onChange={(e) => set("company", e.target.value)} required={form.account_kind === "provider"} data-testid="reg-company" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="reg-email" />
              {claim && (
                <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-emerald-800 flex gap-2" data-testid="claim-banner">
                  <BadgeCheck className="h-4 w-4 shrink-0 text-emerald-600" />
                  <span>
                    Good news — <b>{claim.company_name}</b> is already in our supplier database.
                    Create your account with this email to activate the profile and go online.
                  </span>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} required placeholder="+1 555 0000" data-testid="reg-phone" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="ref">Referral code <span className="text-zinc-400 font-normal">(optional)</span></Label>
              <div className="relative">
                <Gift className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input id="ref" value={form.referral_code} onChange={(e) => set("referral_code", e.target.value.toUpperCase())} placeholder="SV-XXXX" className="pl-9 font-mono uppercase" data-testid="reg-referral-code" />
              </div>
              {refValid === true && <p className="text-[11px] text-emerald-700 mt-1" data-testid="reg-referral-valid">Referral code applied — a Seavanto partner referred you.</p>}
              {refValid === false && <p className="text-[11px] text-amber-700 mt-1" data-testid="reg-referral-invalid">We don't recognise this code. You can still sign up without it.</p>}
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="pwd">Password</Label>
              <Input id="pwd" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required data-testid="reg-password" />
              <p className="text-[11px] text-zinc-500 mt-2">
                {form.account_kind === "provider"
                  ? "You'll land on the Service Provider home. Yacht-related pages unlock once a yacht owner adds you to their crew."
                  : "You'll choose your role (Owner, Manager or Captain) when you create your first yacht — and you can hold different roles on different yachts."}
              </p>
            </div>
          </div>

          {err && <div className="text-sm text-red-600" data-testid="register-error">{err}</div>}

          <Button type="submit" disabled={busy || !form.email || !form.password} className="w-full bg-ink hover:bg-black" data-testid="register-submit">
            {busy ? "Creating…" : "Create account"} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>

          <div className="text-sm text-zinc-600 text-center">
            Already on Seavanto? <Link to="/login" className="text-ink underline" data-testid="link-login">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
